Retorna a lista paginada de campanhas da organização autenticada.

É o endpoint base para dashboards, supervisão externa e sincronização de estado operacional.

<ApiCodeExamples exampleId="campaignsList" />

<ApiResponseExamples exampleId="campaignsList" />

## Query parameters

- `page` (`integer`, opcional): número da página.
  Regra:
  começa em `1`
- `page_size` (`integer`, opcional): quantidade de itens por página.
  Regra:
  default `25`, máximo `100`
- `ordering` (`string`, opcional): campo de ordenação. Use prefixo `-` para ordem decrescente.
  Valores possíveis:
  `created_at`, `updated_at`, `name`, `status`, `campaign_type`, `total_contacts`, `total_calls`, `connected_calls`, `converted_calls`, `started_at`, `completed_at`
- `search` (`string`, opcional): busca textual aplicada em `name` e `description`.
- `status` (`string`, opcional): um ou mais status, separados por vírgula.
  Valores possíveis:
  `draft`, `processing`, `ready`, `active`, `paused`, `completed`, `cancelled`
- `campaign_type` (`string`, opcional): um ou mais tipos de campanha, separados por vírgula.
  Valores possíveis:
  `ai`, `reverse_ivr`
- `ai_agent_provider_id` (`uuid`, opcional): um ou mais provedores, separados por vírgula.
- `ai_agent_id` (`uuid`, opcional): um ou mais agentes, separados por vírgula.
- `created_by` (`uuid`, opcional): um ou mais usuários criadores, separados por vírgula.

## Resposta `200`

### Paginação

- `count` (`integer`): total de campanhas que batem com os filtros.
- `next` (`string|null`): URL completa da próxima página.
- `previous` (`string|null`): URL completa da página anterior.
- `results` (`array<object>`): itens da página atual.

### Campos de cada item em `results`

- `id` (`uuid`): identificador da campanha.
- `canonical_url` (`string`): link canônico da campanha no app Contact-Tel.
- `created_by_id` (`uuid|null`): identificador do usuário que criou a campanha.
- `created_by_name` (`string|null`): nome exibível do usuário que criou a campanha.
- `name` (`string`): nome da campanha.
- `description` (`string`): descrição livre da campanha.
- `status` (`string`): status operacional persistido.
  Valores possíveis:
  `draft`, `processing`, `ready`, `active`, `paused`, `completed`, `cancelled`
- `status_reason` (`string|null`): código público e sanitizado para explicar estados especiais.
  Valores possíveis:
  `import_failed`, `config_error`, `paused`, `null`
- `campaign_type` (`string`): modalidade operacional da campanha.
  Valores possíveis:
  `ai`, `reverse_ivr`
- `ai_agent_provider_id` (`uuid|null`): identificador do provedor associado.
- `ai_agent_provider_name` (`string|null`): nome exibível do provedor associado.
- `ai_agent_provider_type` (`string|null`): tipo do provedor associado.
- `ai_agent_id` (`uuid|null`): identificador do agente associado.
- `ai_agent_name` (`string|null`): nome exibível do agente associado.
- `sip_trunk_id` (`uuid|null`): identificador do SIP trunk associado.
- `sip_trunk_name` (`string|null`): nome exibível do SIP trunk associado.
- `max_concurrent_calls` (`integer`): limite de concorrência configurado para a campanha.
- `tags` (`array<string>`): tags livres associadas à campanha.
- `transfer_target_type` (`string|null`): tipo do alvo de transferência configurado.
  Valores possíveis:
  `user`, `group`, `null`
- `transfer_target_id` (`uuid|null`): identificador público do usuário/grupo alvo de transferência.
- `transfer_target_name` (`string|null`): nome exibível do alvo de transferência.
- `transfer_target_extension` (`string|null`): ramal do alvo quando o alvo é um usuário com ramal ativo.
- `import_status` (`string`): status do pipeline de importação de contatos.
  Valores possíveis:
  `idle`, `processing`, `completed`, `failed`
- `total_contacts` (`integer`): total de contatos materializados na campanha.
- `total_phones` (`integer`): total de telefones materializados na campanha.
- `pending_contacts` (`integer`): contatos ainda pendentes de execução.
- `in_progress_contacts` (`integer`): contatos em execução.
- `reached_contacts` (`integer`): contatos que já tiveram desfecho de alcance.
- `converted_contacts` (`integer`): contatos convertidos.
- `optout_contacts` (`integer`): contatos marcados como opt-out.
- `mismatch_contacts` (`integer`): contatos marcados como mismatch.
- `exhausted_contacts` (`integer`): contatos que esgotaram tentativas/regras de contato.
- `no_valid_phones_contacts` (`integer`): contatos sem telefone válido.
- `skipped_contacts` (`integer`): contatos ignorados/cancelados pelo fluxo operacional.
- `total_calls` (`integer`): total de chamadas vinculadas à campanha.
- `connected_calls` (`integer`): chamadas efetivamente conectadas.
- `conversion_eligible_calls` (`integer`): chamadas elegíveis para cálculo de conversão.
- `failed_calls` (`integer`): chamadas com falha operacional.
- `converted_calls` (`integer`): chamadas consideradas convertidas.
- `ai_billed_seconds` (`integer`): segundos faturáveis líquidos de IA na campanha.
- `ai_gross_billed_seconds` (`integer`): segundos faturáveis brutos de IA na campanha.
- `ai_refunded_seconds` (`integer`): segundos de IA estornados/refundados na campanha.
- `telephony_billed_seconds` (`integer`): segundos faturáveis líquidos de telefonia na campanha.
- `telephony_gross_billed_seconds` (`integer`): segundos faturáveis brutos de telefonia na campanha.
- `telephony_refunded_seconds` (`integer`): segundos de telefonia estornados/refundados na campanha.
- `conversion_rate` (`number`): taxa de conversão calculada sobre chamadas elegíveis.
- `progress_percentage` (`number`): progresso calculado da campanha em percentual.
  Regra:
  faixa de `0` a `100`
- `created_at` (`datetime`): instante de criação.
- `updated_at` (`datetime`): instante da última atualização.
- `started_at` (`datetime|null`): instante em que a campanha foi iniciada, quando aplicável.
- `completed_at` (`datetime|null`): instante em que a campanha foi concluída, quando aplicável.

### Referência de opções

#### `status`

- `draft`: campanha em rascunho, ainda sem execução liberada.
- `processing`: campanha preparando/importando contatos.
- `ready`: campanha pronta para iniciar.
- `active`: campanha em execução.
- `paused`: campanha pausada.
- `completed`: campanha concluída.
- `cancelled`: campanha cancelada.

#### `status_reason`

- `import_failed`: a campanha está bloqueada por falha na importação.
- `config_error`: a campanha está pausada por erro de configuração.
- `paused`: pausa operacional sem erro público específico.
- `null`: não há motivo público adicional para o status atual.

#### `campaign_type`

- `ai`: campanha conduzida por agente de IA.
- `reverse_ivr`: campanha de URA reversa com áudio e DTMF.

#### `ai_agent_provider_type`

- `cloud`: provedor cloud de agente/telefonia.
- `asterisk`: provedor Asterisk/SIP local.
- `sip`: provedor SIP externo.

#### `transfer_target_type`

- `user`: transferência para usuário/operador específico.
- `group`: transferência para grupo de atendimento.
- `null`: campanha sem alvo de transferência configurado.

#### `import_status`

- `idle`: nenhum processamento de importação em andamento.
- `processing`: importação de contatos em andamento.
- `completed`: importação concluída.
- `failed`: importação falhou.

## Erros esperados

- `401 Unauthorized`: token inválido, expirado, revogado, ausente ou bloqueado por IP.
- `403 Forbidden`: a credencial autenticou, mas não possui `campaigns.read`.
- `429 Too Many Requests`: a credencial excedeu o limite permitido na janela atual.

## Quando usar este endpoint

- listar campanhas da organização em integrações de supervisão;
- montar dashboards externos;
- acompanhar progresso operacional sem acessar a interface administrativa.
